/** Fixing round "sites in a subfolder" (the founder, 2026-10-10; DECISIONS "2026-10-11 — Sites in a subfolder"): a connected site is
 *  addressed by its home URL, path included, so a WordPress at `https://example.com/blog/` is reached at its folder and is another site
 *  than `https://example.com/shop/`. These rules write such an address one way, for the connect page, the approval, the exchange and the
 *  screens that name a site. Pure. */

/** a `..` segment as written (the URL parser hides it: `https://h/blog/../shop` and `%2e%2e` both read as `/shop`) */
const DOT_DOT = /[\\/](\.|%2e){2}(?=[\\/?#]|$)/i;
const API_PATH = "/wp-json/omnabase/v1/";

/** Why a text is not a site's home address, in the connect page's words, or null: https, no user name, no query or fragment, no `..`. */
export function siteProblem(url: string): string | null {
  const text = url.trim();
  let u: URL;
  try { u = new URL(text); } catch { return "The link names no site."; }
  if (u.protocol !== "https:") return "The site's address has to start with https.";
  if (u.username || u.password || /[?#]/.test(text) || DOT_DOT.test(text)) return "The site's address has to be its plain home address: no user name, query, fragment or \"..\".";
  return null;
}

/** The site's home address written one way: https, the host lower-cased (port 443 dropped, another port kept so the guard can refuse it),
 *  the path as written without empty segments or a trailing slash, the root without a slash; null for anything `siteProblem` refuses. */
export function siteBase(url: string): string | null {
  if (siteProblem(url)) return null;
  const u = new URL(url.trim());
  const path = u.pathname.split("/").filter(Boolean).join("/");
  return `https://${u.host.toLowerCase()}${path ? `/${path}` : ""}`;
}

/** what the team reads as a site's name: "example.com/blog", or the host alone for a site at the root */
export const siteLabel = (url: string): string | null => siteBase(url)?.slice("https://".length) ?? null;

/** the address of a site's webhook and facts endpoints, as the plugin serves them under the site's home address */
export const pluginUrl = (site: string, path: "events" | "customer"): string => `${site.trim().replace(/\/+$/, "")}${API_PATH}${path}`;

/** an endpoint's name in a question or a refusal: a site's plugin address reads as its site ("example.com/shop"), any other system as its host */
export function endpointLabel(url: string): string {
  const at = url.indexOf(API_PATH);
  const label = at > 0 ? siteLabel(url.slice(0, at)) : null;
  if (label) return label;
  try { return new URL(url).hostname.toLowerCase(); } catch { return url; }
}

/** whether `page` (the way back) lies on the site's host and under its folder, at a segment boundary: `/blog2` is not under `/blog` */
export function underSite(base: string, page: string): boolean {
  let site: URL, back: URL;
  try { site = new URL(base); back = new URL(page.trim()); } catch { return false; }
  if (back.host.toLowerCase() !== site.host) return false;
  const folder = site.pathname.replace(/\/+$/, "");
  return !folder || back.pathname === folder || back.pathname.startsWith(`${folder}/`);
}
